import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  LogOut,
  Wallet,
  TrendingUp,
  Vote,
  Receipt,
  AlertCircle,
  CheckCircle2,
  ShieldCheck,
  ExternalLink,
  Info,
  HelpCircle,
  ChevronDown,
  LineChart as LineChartIcon,
  ArrowUpRight,
  ArrowDownRight,
} from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { SubscriptionsBlock } from '../components/SubscriptionsBlock';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';

const formatCHF = (n) =>
  new Intl.NumberFormat('de-CH', { maximumFractionDigits: 2 }).format(Number(n) || 0);

const formatInt = (n) =>
  new Intl.NumberFormat('de-CH').format(Math.round(Number(n) || 0));

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('de-CH', {
      day: '2-digit', month: 'short', year: 'numeric'
    });
  } catch {
    return iso;
  }
};

const KpiCard = ({ icon: Icon, label, value, sub, accent, testId, tooltip }) => (
  <div
    data-testid={testId}
    className="bg-[#1a1a1a] border border-[#C4A55A]/25 rounded-2xl p-6 hover:border-[#C4A55A]/55 transition-all duration-300 relative group"
  >
    <div className="flex items-center gap-3 mb-4">
      <div className="w-10 h-10 rounded-xl bg-[#C4A55A]/15 border border-[#C4A55A]/40 flex items-center justify-center">
        <Icon className="h-5 w-5 text-[#C4A55A]" />
      </div>
      <span className="text-gray-400 text-xs uppercase tracking-wider flex-1">{label}</span>
      {tooltip && (
        <span
          className="relative cursor-help"
          data-testid={`tooltip-${testId || 'card'}`}
        >
          <HelpCircle className="h-4 w-4 text-gray-500 hover:text-[#C4A55A]" />
          <span className="invisible group-hover:visible absolute right-0 top-6 z-20 w-64 bg-black/95 border border-[#C4A55A]/40 rounded-lg p-3 text-xs text-gray-300 leading-relaxed shadow-xl">
            {tooltip}
          </span>
        </span>
      )}
    </div>
    <div className={`text-3xl font-bold tabular-nums ${accent || 'text-white'}`}>
      {value}
    </div>
    {sub && <div className="text-sm text-gray-400 mt-1">{sub}</div>}
  </div>
);

const INVESTOR_FAQS = [
  {
    q: 'Wann erscheinen meine Aktien im Portfolio?',
    a: 'Sobald Ihre Zeichnung von uns geprüft, die Zahlung bestätigt und die Eintragung im Schweizer Aktienregister (das-aktienregister.ch) abgeschlossen wurde. In der Regel innerhalb von 3–7 Werktagen nach Zahlungseingang.'
  },
  {
    q: 'Was bedeutet der Status „In Prüfung" / „Bestätigt" / „Entwurf"?',
    a: '„In Prüfung" = Zeichnung eingegangen, Zahlung wird erwartet. „Bestätigt" = Zahlung eingegangen, Zeichnung von der Geschäftsleitung freigegeben. „Entwurf" = Vormerkung im Aktienregister, noch nicht final eingetragen.'
  },
  {
    q: 'Wie kann ich neue Aktien zeichnen?',
    a: 'Klicken Sie oben im Block „Meine Zeichnungen" auf „Aktien zeichnen". Sie können entweder eine Stückzahl oder einen CHF-Betrag eingeben. Nach dem Absenden erhalten Sie eine PDF-Bestätigung mit allen Bankdaten.'
  },
  {
    q: 'Wo finde ich die Bankverbindung für die Überweisung?',
    a: 'In der PDF-Bestätigung Ihrer Zeichnung – Sie können diese jederzeit über den Button „PDF" in der Zeichnungs­tabelle erneut herunterladen.'
  },
  {
    q: 'Kann ich Aktien wieder verkaufen?',
    a: 'Die Aktien sind nicht börsen­kotiert. Ein Verkauf an Dritte oder zurück an die Gesellschaft ist nach Absprache mit dem Verwaltungsrat möglich. Bitte kontaktieren Sie office@loewenherz.ag.'
  },
  {
    q: 'Wie sind meine Daten geschützt?',
    a: 'Alle Übertragungen erfolgen TLS-verschlüsselt (HTTPS). Passwörter sind mit bcrypt gehasht, Sitzungen werden über HTTP-only-Cookies abgesichert. Details siehe Datenschutzerklärung.'
  }
];

const ValuationCard = ({ valuation }) => {
  const isUp = (valuation.gainCHF || 0) >= 0;
  const history = Array.isArray(valuation.history) ? valuation.history : [];
  const prices = history.map((h) => Number(h.price) || 0);
  const minP = prices.length ? Math.min(...prices) : 0;
  const maxP = prices.length ? Math.max(...prices) : 1;
  const range = maxP - minP || 1;

  return (
    <section
      data-testid="valuation-card"
      className="bg-gradient-to-br from-[#1a1a1a] via-[#141414] to-[#0f0f0f] border border-[#C4A55A]/30 rounded-2xl p-6 md:p-8 mb-10"
    >
      <div className="flex items-center gap-2 mb-5">
        <LineChartIcon className="h-5 w-5 text-[#C4A55A]" />
        <h2 className="text-xl md:text-2xl font-bold">Wertentwicklung Ihrer Aktien</h2>
        {valuation.latestYear && (
          <span className="text-xs text-gray-500 ml-2">Stand {valuation.latestYear}</span>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-black/40 border border-white/5 rounded-xl p-4" data-testid="val-current">
          <div className="text-xs uppercase tracking-wider text-gray-400 mb-1">Aktueller Wert</div>
          <div className="text-2xl font-bold text-[#C4A55A] tabular-nums">
            CHF {formatCHF(valuation.currentValueCHF)}
          </div>
          <div className="text-xs text-gray-500 mt-1 tabular-nums">
            CHF {formatCHF(valuation.currentPricePerShare)} / Aktie
          </div>
        </div>
        <div className="bg-black/40 border border-white/5 rounded-xl p-4" data-testid="val-paid">
          <div className="text-xs uppercase tracking-wider text-gray-400 mb-1">Einsatz</div>
          <div className="text-2xl font-bold text-white tabular-nums">
            CHF {formatCHF(valuation.paidValueCHF)}
          </div>
          <div className="text-xs text-gray-500 mt-1">Eingezahlter Betrag</div>
        </div>
        <div
          className={`rounded-xl p-4 border ${
            isUp
              ? 'bg-emerald-500/10 border-emerald-500/30'
              : 'bg-red-500/10 border-red-500/30'
          }`}
          data-testid="val-gain"
        >
          <div className="text-xs uppercase tracking-wider text-gray-400 mb-1">Veränderung</div>
          <div className={`text-2xl font-bold tabular-nums flex items-center gap-1 ${isUp ? 'text-emerald-300' : 'text-red-300'}`}>
            {isUp ? <ArrowUpRight className="h-5 w-5" /> : <ArrowDownRight className="h-5 w-5" />}
            CHF {formatCHF(Math.abs(valuation.gainCHF || 0))}
          </div>
          <div className={`text-xs mt-1 tabular-nums ${isUp ? 'text-emerald-400/80' : 'text-red-400/80'}`}>
            {isUp ? '+' : '−'}{formatCHF(Math.abs(valuation.gainPercent || 0))} %
          </div>
        </div>
      </div>

      {history.length >= 2 && (
        <div data-testid="val-history">
          <div className="text-xs uppercase tracking-wider text-gray-400 mb-3">
            Bewertungsverlauf (CHF / Aktie)
          </div>
          <div className="flex items-end gap-3 h-32 px-1">
            {history.map((h) => {
              const heightPct = ((Number(h.price) - minP) / range) * 100;
              const safeHeight = Math.max(8, heightPct);
              return (
                <div key={h.year} className="flex-1 flex flex-col items-center justify-end gap-2">
                  <div className="text-[10px] text-[#C4A55A] tabular-nums">
                    {formatCHF(h.price)}
                  </div>
                  <div
                    className="w-full rounded-t bg-gradient-to-t from-[#C4A55A]/30 via-[#C4A55A]/60 to-[#C4A55A] transition-all"
                    style={{ height: `${safeHeight}%` }}
                    title={`${h.year}: CHF ${h.price}`}
                  />
                  <div className="text-[11px] text-gray-400 tabular-nums">{h.year}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <p className="text-xs text-gray-500 mt-5 leading-relaxed">
        Die Bewertung pro Aktie wird aus dem Schweizer Aktienregister
        (das-aktienregister.ch) übernommen und jährlich aktualisiert. Nicht börsen­kotiert –
        Werte sind indikativ und stellen keine Garantie auf einen Verkaufspreis dar.
      </p>
    </section>
  );
};


const FaqItem = ({ question, answer }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-white/10 rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left bg-black/30 hover:bg-black/50 transition-colors"
        data-testid={`faq-toggle-${question.slice(0, 20).replace(/\s+/g, '-')}`}
      >
        <span className="text-white text-sm md:text-base font-medium">{question}</span>
        <ChevronDown
          className={`h-4 w-4 text-[#C4A55A] transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <div className="px-4 py-3 text-sm text-gray-300 leading-relaxed bg-black/10">
          {answer}
        </div>
      )}
    </div>
  );
};


export const InvestorDashboard = () => {
  const { user, loading: authLoading, logout } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [portfolio, setPortfolio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const TT = {
    de: {
      welcome: 'Willkommen zurück',
      logout: 'Abmelden',
      onboardingTitle: 'Was sehen Sie hier?',
      onboardingBody: 'Dieses Portal ist Ihr persönlicher Bereich bei Löwenherz Invest AG. Sobald Ihre erste Zeichnung bestätigt und im Schweizer Aktienregister eingetragen ist, sehen Sie hier Ihre Aktien, deren Nennwert, Ihre Stimmrechte und alle Transaktionen in Echtzeit. Neue Aktien zeichnen Sie jederzeit unten im Bereich „Meine Zeichnungen".',
      externalAreg: 'Direkt zum Schweizer Aktienregister',
      faqTitle: 'Häufig gestellte Fragen',
      faqMore: 'Weitere Fragen? Schreiben Sie uns an',
      kpi: { shares: 'Aktien', nominal: 'Nennwert (CHF)', votes: 'Stimmrechte', balance: 'Saldo (CHF)' },
    },
    en: {
      welcome: 'Welcome back',
      logout: 'Sign out',
      onboardingTitle: 'What you see here',
      onboardingBody: 'This portal is your personal area at Löwenherz Invest AG. As soon as your first subscription is confirmed and entered in the Swiss share register, you will see your shares, their nominal value, your voting rights and all transactions in real time. Subscribe to new shares any time in the "My subscriptions" section below.',
      externalAreg: 'Open the Swiss share register',
      faqTitle: 'Frequently asked questions',
      faqMore: 'More questions? Write to us at',
      kpi: { shares: 'Shares', nominal: 'Nominal (CHF)', votes: 'Voting rights', balance: 'Balance (CHF)' },
    },
    ru: {
      welcome: 'С возвращением',
      logout: 'Выйти',
      onboardingTitle: 'Что вы видите здесь',
      onboardingBody: 'Этот портал — ваш персональный раздел в Löwenherz Invest AG. Как только ваша первая подписка будет подтверждена и внесена в швейцарский реестр акций, вы увидите свои акции, их номинальную стоимость, права голоса и все транзакции в реальном времени. Новые акции вы можете подписывать в любое время в разделе «Мои подписки» ниже.',
      externalAreg: 'Перейти к швейцарскому реестру акций',
      faqTitle: 'Часто задаваемые вопросы',
      faqMore: 'Остались вопросы? Напишите нам на',
      kpi: { shares: 'Акции', nominal: 'Номинал (CHF)', votes: 'Голоса', balance: 'Баланс (CHF)' },
    },
    vi: {
      welcome: 'Chào mừng trở lại',
      logout: 'Đăng xuất',
      onboardingTitle: 'Bạn thấy gì ở đây',
      onboardingBody: 'Cổng này là khu vực cá nhân của bạn tại Löwenherz Invest AG. Ngay khi đăng ký đầu tiên của bạn được xác nhận và được ghi vào sổ đăng ký cổ đông Thụy Sĩ, bạn sẽ thấy cổ phiếu của mình, giá trị danh nghĩa, quyền biểu quyết và tất cả các giao dịch theo thời gian thực. Đăng ký cổ phiếu mới bất cứ lúc nào trong phần "Đăng ký của tôi" bên dưới.',
      externalAreg: 'Mở sổ đăng ký cổ đông Thụy Sĩ',
      faqTitle: 'Câu hỏi thường gặp',
      faqMore: 'Còn câu hỏi? Viết cho chúng tôi tại',
      kpi: { shares: 'Cổ phiếu', nominal: 'Mệnh giá (CHF)', votes: 'Quyền biểu quyết', balance: 'Số dư (CHF)' },
    },
  };
  const tt = TT[language] || TT.de;

  useEffect(() => {
    if (!authLoading && (!user || user.role !== 'investor')) {
      navigate('/', { replace: true });
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (!user || user.role !== 'investor') return;
    let cancelled = false;
    const fetchData = async () => {
      try {
        const apiBase = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
        const url = (apiBase ? apiBase : '') + '/api/investor/me/portfolio';
        const r = await fetch(url, { credentials: 'include' });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = await r.json();
        if (!cancelled) setPortfolio(data);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Daten konnten nicht geladen werden');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, [user]);

  const handleLogout = async () => {
    await logout();
    toast.success(language === 'de' ? 'Erfolgreich abgemeldet' : 'Logged out');
    navigate('/', { replace: true });
  };

  if (authLoading || !user) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center text-gray-400">
        Lädt…
      </div>
    );
  }

  const stats = portfolio?.stats || {};
  const sh = portfolio?.shareholder;
  const positions = portfolio?.positions || [];
  const linked = !!portfolio?.linked;
  const aregInfo = portfolio?.areg || {};
  const valuation = portfolio?.valuation;

  return (
    <div className="bg-black min-h-screen text-white">
      <Header />

      <main
        data-testid="page-investor-dashboard"
        className="container mx-auto px-4 pt-32 pb-16 max-w-6xl"
      >
        {/* Top bar */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <Link
              to="/"
              className="inline-flex items-center text-[#C4A55A] hover:text-[#D4B56A] text-sm mb-3"
              data-testid="investor-back-link"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Zurück zur Startseite
            </Link>
            <h1 className="text-3xl md:text-4xl font-bold">
              Willkommen, {user.firstName}
            </h1>
            <p className="text-gray-400 mt-1">
              Ihr persönliches Investor-Portal · Löwenherz Invest AG
            </p>
          </div>
          <button
            onClick={handleLogout}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#1a1a1a] border border-[#C4A55A]/30 hover:bg-[#C4A55A]/10 text-gray-200 text-sm transition-colors"
            data-testid="investor-logout-btn"
          >
            <LogOut className="h-4 w-4" />
            Abmelden
          </button>
        </div>

        {/* Onboarding / explanation box */}
        <div
          data-testid="investor-onboarding-box"
          className="mb-8 bg-gradient-to-br from-[#C4A55A]/10 via-[#1a1a1a] to-[#1a1a1a] border border-[#C4A55A]/30 rounded-2xl p-5 md:p-6"
        >
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-[#C4A55A] mt-1 shrink-0" />
            <div>
              <h3 className="text-white font-semibold mb-1">{tt.onboardingTitle}</h3>
              <p className="text-gray-300 text-sm leading-relaxed">
                {tt.onboardingBody}
              </p>
            </div>
          </div>
        </div>

        {/* Areg link state banner */}
        {!loading && portfolio && (
          <div
            data-testid="investor-link-banner"
            className={`mb-8 rounded-xl p-4 flex items-start gap-3 ${
              linked
                ? 'bg-emerald-500/10 border border-emerald-500/30'
                : 'bg-amber-500/10 border border-amber-500/30'
            }`}
          >
            {linked ? (
              <CheckCircle2 className="h-5 w-5 text-emerald-400 mt-0.5 shrink-0" />
            ) : (
              <AlertCircle className="h-5 w-5 text-amber-400 mt-0.5 shrink-0" />
            )}
            <div className="flex-1">
              {linked ? (
                <>
                  <p className="text-emerald-200 font-medium">
                    Verknüpft mit dem Schweizer Aktienregister
                  </p>
                  <p className="text-sm text-emerald-200/80 mt-0.5">
                    Aktionärsnummer <strong>{sh?.number || '—'}</strong> · {sh?.fullName}
                  </p>
                </>
              ) : (
                <>
                  <p className="text-amber-200 font-medium">
                    Noch kein Aktionärs-Eintrag im Aktienregister
                  </p>
                  <p className="text-sm text-amber-200/80 mt-0.5">
                    {aregInfo.message ||
                      'Bitte warten Sie, bis Ihre Zeichnung von uns im Aktienregister erfasst ist.'}
                  </p>
                </>
              )}
            </div>
          </div>
        )}

        {/* KPI grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
          <KpiCard
            icon={Wallet}
            label="Meine Aktien"
            value={loading ? '…' : formatInt(stats.shareCount)}
            sub={linked ? 'aus Aktienregister' : 'noch nicht erfasst'}
            accent="text-white"
            testId="kpi-share-count"
          />
          <KpiCard
            icon={TrendingUp}
            label="Nennwert CHF"
            value={loading ? '…' : formatCHF(stats.cumulatedFaceValue)}
            sub={
              stats.capitalPercent
                ? `${formatCHF(stats.capitalPercent)} % Kapitalanteil`
                : '—'
            }
            accent="text-[#C4A55A]"
            testId="kpi-face-value"
            tooltip="Kapitalanteil = Ihr Nennwert geteilt durch das gesamte ausgegebene Aktienkapital. Dieser Wert kann vom Stimmrechtsanteil abweichen, wenn die Gesellschaft eigene Aktien hält (deren Stimmrechte ruhen gemäss Art. 659a OR)."
          />
          <KpiCard
            icon={Vote}
            label="Stimmrechte"
            value={loading ? '…' : formatInt(stats.voteCount)}
            sub={
              stats.votePercent
                ? `${formatCHF(stats.votePercent)} % Stimmanteil`
                : '—'
            }
            accent="text-white"
            testId="kpi-votes"
            tooltip="Stimmrechtsanteil = Ihre stimmberechtigten Aktien geteilt durch alle aktiven Stimmen. Liegt Ihr Stimmrechts-Anteil höher als Ihr Kapital-Anteil, ist das normalerweise gut: Es bedeutet, dass die laufende Kapitalerhöhung noch nicht vollständig gezeichnet ist und die unverkauften Aktien (AG-Eigenbestand) kein Stimmrecht tragen."
          />
          <KpiCard
            icon={Receipt}
            label="Transaktionen"
            value={loading ? '…' : formatInt(stats.positionCount)}
            sub="Eintragungen"
            accent="text-white"
            testId="kpi-transactions"
          />
        </div>

        {/* Wertentwicklung (Share Value Development) */}
        {!loading && linked && valuation && valuation.currentPricePerShare > 0 && (
          <ValuationCard valuation={valuation} />
        )}

        {/* Subscriptions block (Phase 4) */}
        <SubscriptionsBlock />

        {/* Transactions table */}
        <section className="bg-[#1a1a1a] border border-[#C4A55A]/25 rounded-2xl p-6 md:p-8 mb-10">
          <div className="flex items-center gap-2 mb-5">
            <Receipt className="h-5 w-5 text-[#C4A55A]" />
            <h2 className="text-xl md:text-2xl font-bold">Transaktionshistorie</h2>
          </div>

          {loading && (
            <div className="text-gray-400 text-sm py-6">Lade Transaktionen…</div>
          )}

          {!loading && positions.length === 0 && (
            <div className="text-gray-400 text-sm py-6">
              {linked
                ? 'Noch keine Transaktionen erfasst.'
                : 'Verknüpfung mit Aktienregister erforderlich.'}
            </div>
          )}

          {!loading && positions.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="investor-positions-table">
                <thead>
                  <tr className="text-left text-gray-400 border-b border-white/10">
                    <th className="py-3 pr-4 font-medium">Datum</th>
                    <th className="py-3 pr-4 font-medium">Typ</th>
                    <th className="py-3 pr-4 font-medium">Wertpapier</th>
                    <th className="py-3 pr-4 font-medium text-right">Anzahl</th>
                    <th className="py-3 pr-4 font-medium text-right">Wert (CHF)</th>
                    <th className="py-3 pr-4 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {positions.map((p) => (
                    <tr
                      key={p.id}
                      className="border-b border-white/5 hover:bg-white/[0.02] transition-colors"
                      data-testid={`position-row-${p.id}`}
                    >
                      <td className="py-3 pr-4">{formatDate(p.boughtAt)}</td>
                      <td className="py-3 pr-4">
                        <span
                          className={`inline-flex px-2 py-0.5 rounded-md text-xs font-medium ${
                            p.role === 'buy'
                              ? 'bg-emerald-500/15 text-emerald-300'
                              : 'bg-amber-500/15 text-amber-300'
                          }`}
                        >
                          {p.role === 'buy' ? 'Zukauf' : 'Verkauf'}
                        </span>
                      </td>
                      <td className="py-3 pr-4 text-gray-200">
                        {p.security || '—'}
                      </td>
                      <td className="py-3 pr-4 text-right tabular-nums">
                        {formatInt(p.count)}
                      </td>
                      <td className="py-3 pr-4 text-right tabular-nums text-[#C4A55A]">
                        {formatCHF(p.value)}
                      </td>
                      <td className="py-3 pr-4">
                        {p.isDraft ? (
                          <span className="text-amber-300 text-xs">Entwurf</span>
                        ) : (
                          <span className="text-emerald-300 text-xs inline-flex items-center gap-1">
                            <ShieldCheck className="h-3 w-3" /> Erfasst
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* External Areg link */}
        <div className="text-center text-sm text-gray-400">
          <a
            href="https://www.das-aktienregister.ch/loewenherz-pool-invest-ag/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-[#C4A55A] hover:text-[#D4B56A] transition-colors"
            data-testid="external-areg-link"
          >
            <ExternalLink className="h-4 w-4" />
            {tt.externalAreg}
          </a>
        </div>

        {/* FAQ Section */}
        <section
          data-testid="investor-faq"
          className="mt-12 bg-[#1a1a1a] border border-[#C4A55A]/25 rounded-2xl p-6 md:p-8"
        >
          <div className="flex items-center gap-2 mb-5">
            <HelpCircle className="h-5 w-5 text-[#C4A55A]" />
            <h2 className="text-xl md:text-2xl font-bold">{tt.faqTitle}</h2>
          </div>
          <div className="space-y-3">
            {INVESTOR_FAQS.map((f, i) => (
              <FaqItem key={i} question={f.q} answer={f.a} />
            ))}
          </div>
          <p className="text-gray-400 text-sm mt-6">
            {tt.faqMore}{' '}
            <a href="mailto:office@loewenherz.ag" className="text-[#C4A55A] hover:text-[#D4B56A]">
              office@loewenherz.ag
            </a>
            .
          </p>
        </section>

        {error && (
          <p className="text-red-400 text-sm mt-6 text-center" data-testid="investor-error">
            {error}
          </p>
        )}
      </main>

      <Footer />
    </div>
  );
};
